import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, LogOut, User, ShoppingCart } from 'lucide-react';
import { RetailerUser } from '../services/authService';

interface NavigationProps {
  cartCount: number;
  onCartClick: () => void;
  onLoginClick: () => void;
  retailerUser: RetailerUser | null;
  onLogout: () => void;
}

const Navigation: React.FC<NavigationProps> = ({
  cartCount, onCartClick, onLoginClick, retailerUser, onLogout
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { pathname } = useLocation();

  const navLinks = [
    { label: 'Home', to: '/' },
    { label: 'Shop', to: '/shop' },
    { label: 'About Us', to: '/about' },
    { label: 'News', to: '/news' },
    { label: 'FAQ', to: '/faq' },
    { label: 'Contact', to: '/contact' },
  ];

  const navLinkClass = (to: string) =>
    `text-sm font-medium relative transition-colors duration-200 pb-1 ${
      pathname === to
        ? 'text-brand-red'
        : 'text-brand-text-muted hover:text-brand-red'
    }`;

  const isLoggedIn = !!retailerUser;

  return (
    <>
      {/* Announcement Bar */}
      <div className="bg-brand-dark text-white text-center py-2 text-[13px] font-medium tracking-wide">
        <span className="mr-1">🇨🇦</span> <span className="text-brand-gold font-bold">Now Serving All of Canada</span> — Wholesale for African, Caribbean, Asian & Latin American stores. Apply today!
      </div>

      {/* Main Nav */}
      <nav className="sticky top-0 z-50 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-10 flex items-center justify-between h-[72px]">
          {/* Logo */}
          <Link to="/" className="flex items-center cursor-pointer">
            <img
              src="/images/logo.png"
              alt="The Yaad Shop"
              className="h-[56px] object-contain"
              onError={(e) => {
                const el = e.target as HTMLImageElement;
                el.style.display = 'none';
                (el.nextElementSibling as HTMLElement).style.display = 'flex';
              }}
            />
            <div className="hidden items-center gap-3" style={{ display: 'none' }}>
              <div className="w-[42px] h-[42px] bg-gradient-to-br from-brand-red to-brand-red-dark rounded-[10px] flex items-center justify-center">
                <span className="font-display font-bold text-white text-sm">YS</span>
              </div>
              <div>
                <span className="font-display font-extrabold text-[22px] text-brand-dark leading-none block">The Yaad Shop</span>
                <span className="text-[10.5px] font-medium text-brand-red uppercase tracking-[1.5px] block mt-0.5">Multicultural Wholesale</span>
              </div>
            </div>
          </Link>

          {/* Desktop Nav Links */}
          <ul className="hidden lg:flex items-center gap-7">
            {navLinks.map((link) => (
              <li key={link.to}>
                <Link
                  to={link.to}
                  className={navLinkClass(link.to)}
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center gap-3">
            {isLoggedIn ? (
              <>
                {/* Cart Button */}
                <button
                  onClick={onCartClick}
                  className="relative text-brand-dark hover:text-brand-red transition-colors p-2"
                  aria-label="Shopping cart"
                >
                  <ShoppingCart className="w-5 h-5" />
                  {cartCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 bg-brand-red text-white text-[10px] font-bold w-[18px] h-[18px] rounded-full flex items-center justify-center">
                      {cartCount}
                    </span>
                  )}
                </button>

                {/* User Info */}
                <div className="flex items-center gap-2 bg-brand-light-bg rounded-lg px-3 py-2">
                  <div className="w-7 h-7 bg-brand-red/10 rounded-full flex items-center justify-center">
                    <User className="w-3.5 h-3.5 text-brand-red" />
                  </div>
                  <span className="text-sm font-medium text-brand-dark max-w-[140px] truncate">
                    {retailerUser.businessName}
                  </span>
                </div>

                {/* Logout */}
                <button
                  onClick={onLogout}
                  className="text-sm font-medium text-brand-text-muted hover:text-brand-red transition-colors flex items-center gap-1.5 px-3 py-2"
                  aria-label="Sign out"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden xl:inline">Sign Out</span>
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={onLoginClick}
                  className="text-sm font-semibold text-brand-dark border border-gray-200 rounded-[8px] px-5 py-2.5 hover:border-brand-red hover:text-brand-red transition-colors"
                >
                  Sign In
                </button>
                <Link
                  to="/apply"
                  className="text-sm font-semibold text-white bg-brand-red rounded-[8px] px-5 py-2.5 hover:bg-brand-red-dark transition-all shadow-md shadow-brand-red/25 hover:-translate-y-0.5"
                >
                  Apply for Account
                </Link>
              </>
            )}
          </div>

          {/* Mobile Toggle */}
          <div className="lg:hidden flex items-center gap-2">
            {isLoggedIn && (
              <button
                onClick={onCartClick}
                className="relative text-brand-dark p-2"
                aria-label="Shopping cart"
              >
                <ShoppingCart className="w-5 h-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-brand-red text-white text-[10px] font-bold w-[18px] h-[18px] rounded-full flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </button>
            )}
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-brand-dark p-2">
              {isMobileMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 absolute w-full left-0 z-50 shadow-lg">
            <div className="px-5 pt-4 pb-6 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block w-full text-left py-3 font-medium border-b border-gray-100 ${
                    pathname === link.to ? 'text-brand-red' : 'text-brand-text-muted hover:text-brand-red'
                  }`}
                >
                  {link.label}
                </Link>
              ))}

              {isLoggedIn ? (
                <div className="pt-4 space-y-3">
                  <div className="flex items-center gap-2 bg-brand-light-bg rounded-lg px-3 py-2.5">
                    <div className="w-7 h-7 bg-brand-red/10 rounded-full flex items-center justify-center">
                      <User className="w-3.5 h-3.5 text-brand-red" />
                    </div>
                    <span className="text-sm font-medium text-brand-dark truncate">
                      {retailerUser.businessName}
                    </span>
                  </div>
                  <button
                    onClick={() => { onLogout(); setIsMobileMenuOpen(false); }}
                    className="w-full text-sm font-semibold text-brand-text-muted border border-gray-200 rounded-[8px] px-4 py-2.5 flex items-center justify-center gap-2 hover:text-brand-red hover:border-brand-red transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </button>
                </div>
              ) : (
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => { onLoginClick(); setIsMobileMenuOpen(false); }}
                    className="flex-1 text-sm font-semibold text-brand-dark border border-gray-200 rounded-[8px] px-4 py-2.5"
                  >
                    Sign In
                  </button>
                  <Link
                    to="/apply"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex-1 text-sm font-semibold text-white bg-brand-red rounded-[8px] px-4 py-2.5 text-center"
                  >
                    Apply
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default Navigation;
